// ============================================================
// A BODY'S TRAIT LOOKS ON THE PLATE (plan body-map T1): every look the
// body's nature shows (engine/plate/traitLooks), composed into the plate's
// three places - behind the figure and before it (inside its idle group, so
// they move with it), and over it on the plate - with their defs, and the
// scene the plate's clock writes of them each frame. A plate mounts these;
// a new look plugs in here alone.
// ============================================================

import { artFrame, middleOf, type Box, type Point } from '../../../../engine/plate/frame';
import { figureBounds, regionBox, type PlateArt } from '../../../../engine/plate/figureArt';
import { fieldMoment, type FieldMoment } from '../../../../engine/plate/fieldRing';
import type { Guard } from '../../../../engine/plate/moments';
import { deadLooks, type TraitLooks } from '../../../../engine/plate/traitLooks';
import { hasSpecial } from '../../../../engine/plate/specials';
import type { HitPlay } from '../../usePlateHits';
import { memo, type CSSProperties } from 'react';
import type { WrittenNode } from '../scene';
import tx from '../../../../styles/PlateTraits.module.css';
import { FieldRingBack, FieldRingFront, fieldNodes, fieldStill } from './FieldRing';
import { PsykerBack, PsykerDefs, PsykerFront, PsykerRing, psykerNodes, psykerStill } from './Psyker';
import { BanishFlash, DaemonDefs, DaemonUnder } from './Daemonic';
import { RegenBack, RegenFront, regenNodes, regenStill } from './Regeneration';
import { Fists, ToxicDefs, ToxicFront, type Arms } from './Toxic';
import { ConduitFront, conduitNodes, conduitStill, FearBack, FearDefs, fearNodes, fearStill } from './Echoes';
import { pinVoid, VoidDefs, VoidHole } from './VoidWindow';
import {
  Backtrack,
  IncorporealFront,
  LiftBack,
  NatureDefs,
  NightBack,
  nightNodes,
  nightStill,
  NightVignette,
  PhaseBack,
} from './Natures';
import { bypassOf } from '../../../../engine/plate/daemonic';
import { BACKTRACK } from '../../../../engine/plate/natures';
import { firstLanded } from '../../../../engine/plate/burst';
import type { Frame } from '../../../../engine/plate/calloutLayout';
import { ArmourFront, FrenzyBack, ListenFront, MarkDefs, SturdyFront, SwiftUnder } from './Marks';

/** What a plate draws of a body's looks: their degrees as it is now, and where (the figure's whole bounds). */
export interface TraitDraw extends Omit<TraitLooks, 'toxicFists'> {
  /** Dead and daemonic: banished - the figure collapses and leaves the plate empty. */
  banished: boolean;
  /** The arms the poison's veins climb, when it has natural weapons. */
  toxicArms: Arms | null;
  /** The arms Deadly Natural Weapons' veins climb. */
  dnwArms: Arms | null;
  /** The figure's whole bounds, and each place's anchor (the heart, the chest, a struck place). */
  box: Box;
  anchors: Partial<Record<string, Point>>;
  /** The boxes of the head and the arms (a nightmare's wrong shadow moves them; a blind head listens). */
  parts: Partial<Record<string, Box>>;
  /** A psyker under a Witchbane hit: its power gutters while the hit plays (plan design-gaps, P6). */
  gutter: boolean;
}

/** Once per art: the parse of its polygons is the same for every look and every render. */
const perArt = <T,>(read: (art: PlateArt) => T) => {
  const cache = new WeakMap<PlateArt, T>();
  return (art: PlateArt): T => {
    if (!cache.has(art)) cache.set(art, read(art));
    return cache.get(art) as T;
  };
};

/** The boxes of the head and the arms, as drawn (a figure without one has none). */
const partsOf = perArt((art): Partial<Record<string, Box>> => {
  const ids = ['head', 'rightArm', 'leftArm'] as const;
  return Object.fromEntries(ids.filter((id) => art.regions[id]?.length).map((id) => [id, regionBox(art.regions[id])]));
});

/** The arms' outline pieces and their height (where a poison climbs from the hands), or null without arms. */
const armsOf = perArt((art): Arms | null => {
  const pieces = [...(art.regions.rightArm ?? []), ...(art.regions.leftArm ?? [])];
  if (!pieces.length) return null;
  const [, top, , bottom] = regionBox(pieces);
  return { pieces, top, bottom };
});

/** The arms the veins climb: Toxic's when it has natural weapons, and Deadly Natural Weapons'. */
function veinArms(l: TraitLooks, art: PlateArt) {
  const arms = l.toxicFists || l.dnw ? armsOf(art) : null;
  return { toxicArms: l.toxicFists ? arms : null, dnwArms: l.dnw ? arms : null };
}

/** The looks to draw: a field that an attack playing has just overloaded stays drawn until its ring has shattered. */
export function traitDraw(
  looks: TraitLooks,
  art: PlateArt,
  guard: Guard,
  play: HitPlay | null,
  dead: boolean,
): TraitDraw {
  const shown = shownLooks(looks, guard, play, dead);
  return {
    ...shown,
    // The veins read the toxic fists the shown looks leave out.
    ...veinArms(dead ? deadLooks(looks) : looks, art),
    box: artFrame(art, figureBounds).box,
    anchors: art.anchors,
    parts: shown.nightmares || shown.blind ? partsOf(art) : {},
  };
}

/**
 * The looks a plate shows this moment, whatever it is drawn from (a figure's art, or only a box): dead, none but a
 * daemon's banishment; a field shattering by an overloading hit still up for it; a psyker guttering under a
 * Witchbane hit. One rule for both draws (the Track P simplify: the box draw had dropped the shattering field).
 */
function shownLooks(looks: TraitLooks, guard: Guard, play: HitPlay | null, dead: boolean) {
  const shattering = play?.burst.hits.some((h) => h.overloaded) ?? false;
  const shown = dead ? deadLooks(looks) : looks;
  const { toxicFists: _fists, ...rest } = shown;
  return {
    ...rest,
    field: shown.field || (shattering ? guard.rating : 0),
    banished: dead && shown.daemon > 0,
    gutter: shown.psy > 0 && hasSpecial(play?.burst.specials ?? [], 'Witchbane'),
  };
}

/**
 * The looks as a drawing WITHOUT traced art draws them (the goo; plan design-gaps, P8): round its box, in the
 * figure's units. A look made of the art's own lines (a psyker's glowing body, the veins up the arms, the senses on
 * the head) has nothing to draw there and draws nothing; the rest - the rune ring, the field, the corona, fear,
 * regeneration, lift, the marks round the box - wear as on a figure.
 */
export function boxDraw(looks: TraitLooks, box: Box, guard: Guard, play: HitPlay | null, dead: boolean): TraitDraw {
  return { ...shownLooks(looks, guard, play, dead), toxicArms: null, dnwArms: null, box, anchors: {}, parts: {} };
}

/**
 * Whether any look moves on the clock (it must run while one does). Not Phase, Warp Instability, Frenzied or Blind:
 * theirs are CSS animations, and a hit runs the clock on its own. Not From Beyond: its sky is still - the clock pins it
 * only while something else moves the hole.
 */
export const looksMove = (d: TraitDraw) =>
  [d.psy, d.field, d.daemon, d.regen, d.toxic, d.fear].some((x) => x > 0) ||
  [d.conduit, d.incorporeal, d.lift, d.nightmares, d.swift, d.dnwArms].some(Boolean);

/** Whether a look draws from the figure's solid silhouette (`<u>-sil`): a shadow, an outline, a hole. */
export const needsSilhouette = (d: TraitDraw) =>
  d.daemon > 0 || d.fear > 0 || d.natArmour > 0 || d.beyond || d.incorporeal || d.frenzied;

/**
 * The figure's own style under its looks: a toxic body's blood drains green (Blood Loss reads `--plate-blood`); an
 * incorporeal one is faint; Warp Instability's backtrack thins it from its moment.
 */
function figureStyle(d: TraitDraw, play: HitPlay | null): CSSProperties | undefined {
  const at = d.warpInst && play ? firstLanded(play.burst)?.at : undefined;
  const style = {
    ...(d.toxic > 0 ? { '--plate-blood': 'var(--plate-toxic)' } : {}),
    ...(d.incorporeal ? { opacity: 0.22 } : {}),
    ...(at !== undefined ? { '--backtrack-at': `${(at + BACKTRACK.delay).toFixed(2)}s` } : {}),
  };
  return Object.keys(style).length ? (style as CSSProperties) : undefined;
}

/** Where the heart beats: a little to the figure's left of its chest point (the viewer's right). */
const heartOf = (d: TraitDraw): Point => {
  const [x, y] = d.anchors.body ?? [(d.box[0] + d.box[2]) / 2, d.box[1] + (d.box[3] - d.box[1]) * 0.4];
  return [x + 60, y - 140];
};
const chestOf = (d: TraitDraw): Point => d.anchors.body ?? middleOf(d);

/** What a landed attack does to the whole figure: it passes through the Incorporeal, or sets off the backtrack. */
function struckClass(d: TraitDraw, play: HitPlay | null): string | undefined {
  if (!play || !firstLanded(play.burst)) return undefined;
  if (d.incorporeal && !bypassOf(play.burst.specials)) return tx.passThrough;
  return d.warpInst ? tx.backtrackFigure : undefined;
}

/**
 * How the looks dress the figure itself, one nested layer each, outermost first: a daemon's crawling filter and a
 * banished one's collapse; the figure's style (faint, green-blooded); what a landed hit does to it; a Frenzied body
 * straining. Each animation owns its own group - two `animation`s (or an animated and an inline opacity) on one
 * element would cancel each other - so a hit's jolt and the tremor play together and opacities multiply.
 */
export function figureLook(u: string, d: TraitDraw, play: HitPlay | null) {
  return {
    filter: d.daemon > 0 ? `url(#${u}-wrong)` : undefined,
    className: d.banished ? tx.banished : undefined,
    style: figureStyle(d, play),
    struck: struckClass(d, play),
    standing: d.frenzied ? tx.straining : undefined,
  };
}

/** The looks' shared soft glow, and each look's own defs. */
export const TraitDefs = memo(function TraitDefs({ u, d }: { u: string; d: TraitDraw }) {
  // Not gated on looksMove: a still look (Natural Armour's scales, Frenzied's outline) needs its defs too.
  return (
    <>
      <filter id={`${u}-tglow`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation={10} result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      {d.psy > 0 && <PsykerDefs u={u} r={d.psy} />}
      {d.daemon > 0 && <DaemonDefs u={u} power={d.daemon} />}
      {d.toxic > 0 && <ToxicDefs u={u} x={d.toxic} heart={heartOf(d)} />}
      {d.fear > 0 && <FearDefs u={u} />}
      {d.beyond && <VoidDefs u={u} />}
      <NatureDefs u={u} incorporeal={d.incorporeal} />
      <MarkDefs u={u} scales={d.natArmour} tense={d.frenzied} />
    </>
  );
});

/** Under the figure, outside it (copies of its lines that do not sway with it): the daemon's corona, echo, shadow. */
export const TraitUnder = memo(function TraitUnder({ u, d, frame }: { u: string; d: TraitDraw; frame: Frame }) {
  return (
    <>
      {/* Over the frame the plate actually draws (its tight one, or the goo's in a figure's units): the Track P review. */}
      {d.nightmares && <NightVignette u={u} frame={frame} />}
      {d.swift && <SwiftUnder u={u} box={d.box} />}
      {d.daemon > 0 && <DaemonUnder u={u} power={d.daemon} box={d.box} dead={d.banished} />}
    </>
  );
});

/** Behind the figure, moving with it. */
export const TraitBack = memo(function TraitBack({ u, d, play }: { u: string; d: TraitDraw; play: HitPlay | null }) {
  return (
    <>
      {d.frenzied && <FrenzyBack u={u} />}
      {d.nightmares && <NightBack u={u} parts={d.parts} />}
      {d.lift && <LiftBack u={u} kind={d.lift} box={d.box} />}
      {d.phase && <PhaseBack u={u} />}
      {d.warpInst && play && <Backtrack u={u} play={play} box={d.box} />}
      {d.fear > 0 && <FearBack u={u} x={d.fear} box={d.box} />}
      {d.regen > 0 && <RegenBack u={u} x={d.regen} box={d.box} />}
      {d.field > 0 && <FieldRingBack rating={d.field} box={d.box} />}
      {d.psy > 0 && <PsykerBack u={u} r={d.psy} box={d.box} />}
    </>
  );
});

/** Before the figure, moving with it. */
export const TraitFront = memo(function TraitFront({ u, d, play }: { u: string; d: TraitDraw; play: HitPlay | null }) {
  return (
    <>
      {d.regen > 0 && <RegenFront u={u} x={d.regen} box={d.box} />}
      {d.toxic > 0 && <ToxicFront u={u} arms={d.toxicArms} play={play} place={d} />}
      {d.conduit && <ConduitFront u={u} chest={chestOf(d)} />}
      {d.psy > 0 && <PsykerFront u={u} r={d.psy} box={d.box} />}
      {d.field > 0 && <FieldRingFront u={u} rating={d.field} box={d.box} />}
      {d.incorporeal && <IncorporealFront u={u} />}
      <MarksFront u={u} d={d} play={play} />
    </>
  );
});

/** Before the figure, the rest of its traits' marks: scales, planted feet, blood-red fists, listening. */
function MarksFront({ u, d, play }: { u: string; d: TraitDraw; play: HitPlay | null }) {
  return (
    <>
      {d.natArmour > 0 && <ArmourFront u={u} x={d.natArmour} play={play} place={d} />}
      {d.sturdy && <SturdyFront box={d.box} play={play} />}
      {d.dnwArms && <Fists u={u} arms={d.dnwArms} kind="dnw" />}
      {d.blind && d.parts.head && <ListenFront u={u} head={d.parts.head} />}
    </>
  );
}

/** On the figure itself, moving with every hit (From Beyond's window to the void). */
export const TraitOn = memo(function TraitOn({ u, d }: { u: string; d: TraitDraw }) {
  return d.beyond ? <VoidHole u={u} box={d.box} /> : null;
});

/** Over the figure, on the plate: the psyker's rune ring (as the bench drew it), a daemon's banishing flash. */
export const TraitOver = memo(function TraitOver({ u, d }: { u: string; d: TraitDraw }) {
  return (
    <>
      {d.psy > 0 && <PsykerRing u={u} r={d.psy} box={d.box} gutter={d.gutter} />}
      {d.banished && <BanishFlash box={d.box} />}
    </>
  );
});

/** A look the plate's clock moves: when it is on, what it writes at a moment, and its still form. */
interface Clocked {
  on: (d: TraitDraw) => boolean;
  at: (d: TraitDraw, t: number, field: FieldMoment) => WrittenNode[];
  still: (d: TraitDraw) => WrittenNode[];
}

/** Every look with a scene for the clock (the rest loop on tracks or CSS on their own). */
const CLOCKED: Clocked[] = [
  { on: (d) => d.psy > 0, at: (d, t) => psykerNodes(d.psy, d.box, t), still: (d) => psykerStill(d.psy, d.box) },
  {
    on: (d) => d.field > 0,
    at: (d, t, m) => fieldNodes(d.field, d.box, t, m),
    still: (d) => fieldStill(d.field, d.box),
  },
  { on: (d) => d.regen > 0, at: (d, t) => regenNodes(d.regen, d.box, t), still: (d) => regenStill(d.regen, d.box) },
  { on: (d) => d.fear > 0, at: (d, t) => fearNodes(d.fear, d.box, t), still: (d) => fearStill(d.fear, d.box) },
  { on: (d) => d.conduit, at: (_, t) => conduitNodes(t), still: () => conduitStill() },
  { on: (d) => d.nightmares, at: (d, t) => nightNodes(d.parts, t), still: (d) => nightStill(d.parts) },
];

/**
 * What the plate's clock does with the looks: writes them at `t` (with the attack `elapsed(t)` seconds in, if one
 * plays), writes their still form when it stops, and pins the void's sky after each frame (what a scene cannot say).
 */
export function traitFrame(d: TraitDraw, u: string, play: HitPlay | null, elapsed: (t: number) => number) {
  const live = CLOCKED.filter((l) => l.on(d));
  return {
    at: (t: number): WrittenNode[] => {
      const field = fieldMoment(play?.burst ?? null, elapsed(t));
      return live.flatMap((l) => l.at(d, t, field));
    },
    still: (): WrittenNode[] => live.flatMap((l) => l.still(d)),
    pin: (root: SVGSVGElement, reset = false) => {
      if (d.beyond) pinVoid(root, u, reset);
    },
  };
}

export type TraitFrame = ReturnType<typeof traitFrame>;
